import api from './api';
import { isAxiosError } from 'axios';
import { type GreenBeanProfile, type CreateGreenBeanProfileDTO, type UpdateGreenBeanProfileDTO } from '../types/greenBean';
import { cache } from '../utils/cache';

const BASE_URL = '/green-beans';

export const greenBeanService = {
  getAll: async (): Promise<GreenBeanProfile[]> => {
    return cache.fetch('greenbeans_all', async () => {
      const response = await api.get(BASE_URL);
      return response.data || [];
    });
  },

  getById: async (id: string): Promise<GreenBeanProfile> => {
    return cache.fetch(`greenbeans_${id}`, async () => {
      const response = await api.get(`${BASE_URL}/${id}`);
      return response.data;
    });
  },

  getProfileByEntity: async (entityType: string, entityId: string): Promise<GreenBeanProfile | null> => {
    try {
      const response = await api.get(`${BASE_URL}/by-entity/${entityType}/${entityId}`);
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getByLot: async (lotId: string): Promise<GreenBeanProfile | null> => {
    try {
      const response = await api.get(`${BASE_URL}/by-lot/${lotId}`);
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  create: async (data: CreateGreenBeanProfileDTO): Promise<GreenBeanProfile> => {
    const response = await api.post(BASE_URL, data);
    // Invalidate all green bean related caches
    cache.invalidate('greenbeans');
    cache.delete('greenbeans_all');
    return response.data;
  },

  update: async (id: string, data: UpdateGreenBeanProfileDTO): Promise<GreenBeanProfile> => {
    const response = await api.put(`${BASE_URL}/${id}`, data);
    cache.delete(`greenbeans_${id}`);
    cache.delete('greenbeans_all');
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`);
    cache.delete(`greenbeans_${id}`);
    cache.delete('greenbeans_all');
  }
};
export type { GreenBeanProfile };

